import { Button, Switch } from '@mui/material';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import React, { ReactNode, useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { AgendaEntry, SpeakerEntry } from '../../../../../interfaces/pagebuilder';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import validationBuilder from '../../../../../static/validations';
import { CustomTranslationForm } from '../../../../common/CustomTranslationForm';
import VyButton from '../../../../common/VyButton';
import VyColorPickerV2 from '../../../../common/VyColorPickerV2';
import VyDateTimePicker from '../../../../common/VyDateTimePicker';
import VyModal from '../../../../common/VyModal';
import VyTextField from '../../../../common/VyTextField';
import VyUpload from '../../../../common/VyUpload';
import ButtonTextEditModal, { ButtonTexts } from './ButtonTextEditModal';
import { SpeakerSelect } from './SpeakerSelect';
import { isMbwOrVbw } from '../../../../../utils/functions';

interface AgendaEditFormProps {
  selectedEntry?: AgendaEntry;
  availableSpeakers?: SpeakerEntry[];
  onSubmit: (props: any) => void;
  isModal?: boolean;
  isModalOpen?: boolean;
  onCloseModal?: () => void;
}

const VyRichTextEditor = dynamic(() => import('../../../../common/VyRichTextEditor'), {
  ssr: false,
  loading: () => <p>loading...</p>,
});

const AgendaEditFormContainer = ({
  isModal = false,
  open = false,
  onClose,
  children,
}: {
  isModal?: boolean;
  open?: boolean;
  onClose?: () => void;
  children?: ReactNode;
}) => {
  if (isModal)
    return (
      <VyModal onClose={() => onClose?.()} open={open} disableEnforceFocus>
        {() => <>{children}</>}
      </VyModal>
    );
  return <>{children}</>;
};

export const AgendaEditForm = (props: AgendaEditFormProps) => {
  const { selectedEntry, onSubmit, availableSpeakers, isModal, isModalOpen, onCloseModal } = props;
  const event = useSelector((state) => state.common.event);

  const [showRichText, setShowRichText] = useState<boolean>(false);
  const [image, setImage] = useState<string | null>(selectedEntry?.image || null);
  const [additionalText, setAdditionalText] = useState<string | null>(selectedEntry?.additionalText || null);
  const [buttonTextEditModalOpen, setButtonTextEditModalOpen] = useState(false);
  const [buttonTexts, setButtonTexts] = useState<ButtonTexts | undefined>(selectedEntry?.buttonTexts);

  const methods = useForm({ mode: 'all' });
  const { t } = useTranslation();
  const validation = validationBuilder(t);

  const watchSpeakers: any = methods.watch('speakers', []);

  function submit(props: any) {
    const submitData = {
      ...props,
      additionalText: additionalText,
      image: image,
      speakers: watchSpeakers ?? [],
      buttonTexts: buttonTexts,
    };
    methods.reset();
    setImage(null);
    setAdditionalText(null);
    onSubmit(submitData);
  }

  useEffect(() => {
    methods.reset({ ...selectedEntry, speakers: selectedEntry?.speakers ?? [] });
    setShowRichText(true);
  }, [selectedEntry]);

  return (
    <AgendaEditFormContainer isModal={isModal} open={isModalOpen} onClose={() => onCloseModal?.()}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(submit)} className="flex flex-col">
          <div className="flex flex-col p-4 overflow-scroll">
            <div className="flex flex-col space-y-2" style={{ maxHeight: isModal && selectedEntry ? '60vh' : '100%' }}>
              <div className="flex gap-8">
                <Controller
                  control={methods.control}
                  name="timestamp"
                  defaultValue={selectedEntry?.timestamp || new Date(event!.datetime_start)}
                  render={({ field: { onChange, value } }) => <VyDateTimePicker defaultDate={value} onChange={(value) => onChange(value?.toISOString())} />}
                />
                {isMbwOrVbw(event?.organization._id!) && (
                  <Controller
                    control={methods.control}
                    name="hideTime"
                    defaultValue={selectedEntry?.hideTime}
                    render={({ field: { onChange, value } }) => (
                      <div className="flex items-center">
                        <p>{t(`pagebuilder:hide-time`)}</p>
                        <Switch defaultChecked={value} onChange={onChange} />
                      </div>
                    )}
                  />
                )}
              </div>
              <Controller
                control={methods.control}
                name={`title`}
                rules={{
                  minLength: { value: 3, message: t(`pagebuilder:entry-error-title-length`) },
                  required: t(`pagebuilder:entry-error-title-required`),
                }}
                render={({ field }) => <VyTextField inputProps={{ ...field }} label={t(`pagebuilder:entry-title`)} fullWidth />}
              />
              <CustomTranslationForm control={methods.control} watch={methods.watch} fieldName="titleTranslations" />

              <Controller
                control={methods.control}
                name={`description`}
                render={({ field }) => <VyTextField inputProps={{ ...field }} label={t(`pagebuilder:entry-description`)} fullWidth />}
              />
              <CustomTranslationForm control={methods.control} watch={methods.watch} fieldName="descriptionTranslations" />

              <div className="flex flex-row justify-between w-full">
                <div>
                  <span>{t(`pagebuilder:entry-title-color`)}</span>
                  <Controller
                    control={methods.control}
                    name="titleColor"
                    defaultValue={selectedEntry?.titleColor}
                    render={({ field: { onChange, value } }) => <VyColorPickerV2 defaultValue={value} onChange={(color) => onChange(color)} />}
                  />
                </div>
                <div>
                  <span>{t(`pagebuilder:entry-description-color`)}</span>
                  <Controller
                    control={methods.control}
                    name="descriptionColor"
                    defaultValue={selectedEntry?.descriptionColor}
                    render={({ field: { onChange, value } }) => <VyColorPickerV2 defaultValue={value} onChange={(color) => onChange(color)} />}
                  />
                </div>
              </div>

              <Controller
                control={methods.control}
                name={`url`}
                rules={validation('urlWithoutProtocol')}
                render={({ field }) => <VyTextField inputProps={{ ...field }} label="URL" fullWidth />}
              />

              <Button variant="outlined" color="primary" fullWidth onClick={() => setButtonTextEditModalOpen(true)}>
                {t('pagebuilder:button-texts')}
              </Button>

              <span className="text-base text-slate-500">{t(`pagebuilder:entry-additional-text`)}</span>
              <input type="hidden" {...methods.register('additionalText')} defaultValue={selectedEntry?.additionalText || ''} />
              {showRichText && <VyRichTextEditor defaultValue={additionalText || ''} onBlur={(text) => setAdditionalText(text)} />}
              <input type="hidden" {...methods.register('image')} defaultValue={selectedEntry?.image} />
              <span className="mt-2">{t(`pagebuilder:entry-image`)}</span>
              <VyUpload
                defaultFileId={image}
                showFileUploadProgress={true}
                acceptedFileTypes="image/*"
                randomExternalImageButton={false}
                onChange={(f) => {
                  setImage(f);
                }}
                showPreview={true}
              />

              <SpeakerSelect availableSpeakers={availableSpeakers} watchSpeakers={watchSpeakers} />
              <ButtonTextEditModal
                open={buttonTextEditModalOpen}
                onClose={() => setButtonTextEditModalOpen(false)}
                defaultValue={buttonTexts}
                onSubmit={(buttonTexts) => setButtonTexts(buttonTexts)}
              />
              {!(isModal && selectedEntry) && (
                <>
                  <VyButton isSaveButton style={{ marginTop: '1rem' }} type="submit">
                    {selectedEntry ? t('pagebuilder:save-changes') : t('pagebuilder:add-new-entry')}
                  </VyButton>
                  {methods.formState.errors &&
                    Object.values(methods.formState.errors).map((error) => error && <p key={error.message?.toString()}>{error.message?.toString()}</p>)}
                </>
              )}
            </div>
          </div>

          {isModal && selectedEntry && (
            <>
              <VyButton isSaveButton style={{ marginTop: '1rem' }} type="submit">
                {t('pagebuilder:save-changes')}
              </VyButton>
              {methods.formState.errors &&
                Object.values(methods.formState.errors).map((error) => error && <p key={error.message?.toString()}>{error.message?.toString()}</p>)}
            </>
          )}
        </form>
      </FormProvider>
      <style jsx>
        {`
          .add-entry {
            display: flex;
            flex-direction: column;
          }
        `}
      </style>
    </AgendaEditFormContainer>
  );
};
